import { useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { IconActivity, IconAlertTriangle, IconChartBar, IconChecklist, IconChevronLeft, IconExternalLink, IconBuilding, IconId, IconInfoCircle, IconLink, IconPencil, IconPhone, IconPlus, IconTrash, IconShieldCheck, IconTag, IconUserCircle, IconUsers, IconTruck, IconShip, IconEye, IconKey, IconEyeOff, IconCopy, IconFileText, IconSearch } from '@tabler/icons-react';
import { IconBell, IconDots, IconBuildingBank, IconPlayerPause, IconStar, IconClockHour3, IconDeviceFloppy } from '@tabler/icons-react';
import { DataError, useList, useRecord } from '@hana/data';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { useT } from '@hana/i18n';
import { Button, Chip, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { Av, Card, Kv, RefundTab } from './CustomerDetail';
import { partnerView, type Partner } from './partner';
import s from './customer-detail.module.css';

// Mint-Partner (1536) · tab Tổng quan: trái Pháp lý · Hiệu suất · Hoạt động; phải Việc cần làm · Thông tin chính · Vai & loại · Người phụ trách · Liên kết.
// Chỉ đọc, không tiền. Các tab còn lại (Liên hệ, Tài khoản nhận tiền, …) làm ở việc sau — bấm báo toast.

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

const TABS = [
  { key: 'ov', label: 'crm.pd.tab.ov' },
  { key: 'ct', label: 'crm.pd.tab.ct' },
  { key: 'bank', label: 'crm.pd.tab.bank' },
  { key: 'pay', label: 'crm.pd.tab.pay' },
  { key: 'lcc', label: 'crm.pd.tab.lcc' },
  { key: 'portal', label: 'crm.pd.tab.portal' },
  { key: 'wiki', label: 'crm.pd.tab.wiki' },
  { key: 'refund', label: 'crm.pd.tab.refund' },
  { key: 'hist', label: 'crm.pd.tab.hist' },
];

export function PartnerDetail() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { partnerId } = useParams({ strict: false }) as { partnerId?: string };
  const { data, error, isLoading, isError, refetch } = useRecord('crmPartner', partnerId);
  const all = useList('crmPartner');
  const forced = readState();
  const [tab, setTab] = useState('ov');
  const [payT, setPayT] = useState<'net' | 'day' | 'eta' | null>(null);
  const [netV, setNetV] = useState<string | null>(null);
  const [cutV, setCutV] = useState('25');
  const [etaV, setEtaV] = useState('3');
  const [shownPw, setShownPw] = useState<Record<string, boolean>>({});
  const noWrite = () => toast({ msg: t('crm.noWrite') });

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (!data) state = 'empty';
  const ready = state === 'ready' && !!data;
  const p: Partner | undefined = data ?? undefined;
  const v = p ? partnerView(p) : null;
  const tabs = TABS.map((tb) => ({ ...tb, label: t(tb.label), n: tb.key === 'ct' ? p?.contacts : tb.key === 'bank' ? p?.banks.length : undefined }));

  return (
    <Page kind="table">
      <PageHeader
        title={partnerId ?? 'Đối tác'}
        lead={
          <Link to="/ezy/partners/$partnerId" params={{ partnerId: 'YANGMING' }} className={s.back} aria-label={t('crm.at.30')} title={t('crm.at.30')}>
            <IconChevronLeft size={18} stroke={2} aria-hidden="true" />
          </Link>
        }
        meta={
          ready && p ? (
            <>
              <Chip>{p.roles.join(' · ') + (p.type ? ` · ${p.type}` : '')}</Chip>
              <Chip tone={p.approved ? 'ok' : 'warn'}>{p.approved ? 'Đã duyệt' : 'Chờ duyệt hồ sơ'}</Chip>
            </>
          ) : undefined
        }
        sub={
          ready && p ? (
            <span className={s.subRow}>
              <span className={s.subItem} title={t('crm.pd.mst', { v: p.mst })}><IconId size={14} stroke={1.8} aria-hidden="true" />{p.mst}</span>
              <span className={s.subItem} title={p.name}><IconBuilding size={14} stroke={1.8} aria-hidden="true" />{p.name}</span>
            </span>
          ) : undefined
        }
        actions={
          <>
            <select className={s.pickSel} aria-label={(all.data?.items ?? []).map((x: Partner) => x.id).join(' ')} value={partnerId} onChange={(e) => void navigate({ to: '/ezy/partners/$partnerId', params: { partnerId: e.target.value } })}>
              {(all.data?.items ?? []).map((x: Partner) => (
                <option key={x.id} value={x.id}>
                  {x.id}
                </option>
              ))}
            </select>
            <HeaderTool label={t('crm.at.31')} icon={IconBell} />
            <HeaderTool label={t('crm.at.32')} icon={IconDots} onClick={noWrite} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>
              {t('crm.x.askPartnerPrice')}
            </Button>
          </>
        }
      />
      <div className={s.page}>
        {ready && <Segmented label={t('crm.at.33')} value={tab} onChange={(k) => (k === 'ov' || k === 'ct' || k === 'bank' || k === 'pay' || k === 'lcc' || k === 'portal' || k === 'wiki' || k === 'hist' ? setTab(k) : toast({ msg: 'Tab này chưa mở ở bản này' }))} items={tabs} />}
        <StateView
          state={state === 'ready' && !data ? 'empty' : state}
          title={state === 'empty' || !data ? 'Không tìm thấy đối tác' : undefined}
          desc={state === 'empty' || !data ? 'Đối tác này không còn trong danh bạ.' : undefined}
          icon={state === 'empty' || !data ? <IconUsers size={26} stroke={1.8} /> : undefined}
          action={state === 'error' ? <Button onClick={() => void refetch()}>{t('crm.cdt.thuLai')}</Button> : undefined}
          skeleton={<Skeleton rows={9} />}
        >
          {p && v && tab === 'ov' && (
            <div className={s.grid}>
              <div className={s.col}>
                <Card title={t('crm.at.34')} icon={IconShieldCheck} hint={t('crm.at.35')} actions={<button type="button" className={s.fold} aria-label={t('crm.at.36')} title={t('crm.at.36')} onClick={noWrite}><IconPencil size={16} stroke={1.8} aria-hidden="true" /></button>}>
                  <div className={s.ptLegal}><Kv rows={v.legal} /></div>
                </Card>
                <Card title={t('crm.at.37')} label={t('crm.at.38')} icon={IconChartBar} hint={t('crm.at.39')} actions={<span className={s.lb}>{t('crm.cdt.6Thang')}</span>}>
                  {p.jobs > 0 ? (
                    <>
                      <div className={s.ptKpi}>
                        {v.perfKpi.map((k) => (
                          <div key={k.k}>
                            <div className={s.lb}>{k.k}</div>
                            <div>
                              <b>{k.v}</b>
                              {k.delta && <Chip tone={k.up ? 'ok' : 'err'}>{k.delta}</Chip>}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className={s.ptBars} role="img" aria-label={t('crm.pd.jobMonth', { id: p.id, v: v.perf.map((r) => `${r.month} ${r.n}`).join(', ') })}>
                        {v.perf.map((r, i) => {
                          const max = Math.max(1, ...v.perf.map((x) => x.n));
                          return (
                            <div key={r.month} className={s.ptBarCol}>
                              <b>{r.n}</b>
                              <div className={`${s.ptBarFill} ${i === 5 ? s.peak : ''}`} style={{ height: `${Math.max(3, Math.round((r.n / max) * 60))}px` }} title={t('crm.pd.nJob', { m: r.month, n: r.n })} />
                            </div>
                          );
                        })}
                      </div>
                      <div className={s.ptMonths}>{v.perf.map((r) => <span key={r.month}>{r.month}</span>)}</div>
                    </>
                  ) : (
                    <div className={s.empty}>{t('crm.cdt.chuaDungTrongJob')}</div>
                  )}
                </Card>
                <Card title={t('crm.at.40')} icon={IconActivity} hint={t('crm.at.41')} defaultOpen={false}>
                  {v.history.map((h) => (
                    <div key={h.k} className={s.ptHist}>
                      <span className={s.ptDot} aria-hidden="true" />
                      <div>
                        <div>{h.k}</div>
                        <div className={s.lb}>{h.when} · {h.who}</div>
                      </div>
                    </div>
                  ))}
                </Card>
              </div>
              <div className={s.col} role="region" aria-label={t('crm.at.42')}>
                <Card title={t('crm.at.20')} icon={IconChecklist} hint={t('crm.at.h2')} actions={v.todo.length ? <Chip tone="warn">{v.todo.length}</Chip> : undefined}>
                  {v.todo.length === 0 && <div className={s.empty}>{t('crm.cdt.khongConViecNao')}</div>}
                  {v.todo.map((t) => (
                    <div key={t.k} className={s.todo}>
                      <IconAlertTriangle size={16} stroke={1.9} className={t.tone === 'err' ? s.errTx : s.warnTx} aria-hidden="true" />
                      <div className={s.todoTx}>
                        <div title={t.k}>{t.k}</div>
                        <div className={s.lb}>
                          <Av text={t.who} /> {t.who} · {t.due}
                        </div>
                      </div>
                      <Button onClick={noWrite}>{t.act}</Button>
                    </div>
                  ))}
                </Card>
                <Card title={t('crm.at.21')} icon={IconInfoCircle} hint={t('crm.at.43')}>
                  <Kv stack rows={v.info.map(([k, val, tone]) => [k, <span key={k} className={tone ? s.warnTx : undefined}>{val}</span>])} />
                </Card>
                <Card title={t('crm.at.44')} label={t('crm.at.45')} icon={IconTruck} hint={t('crm.at.46')} actions={<Button onClick={noWrite} icon={<IconTag size={14} aria-hidden="true" />}>{t('crm.cdt.ganLoai')}</Button>}>
                  <div className={s.chips}>
                    {v.roleChips.map((c) => (
                      <Chip key={c.k} tone={c.warn ? 'warn' : c.pri ? 'info' : undefined}>{c.k}</Chip>
                    ))}
                  </div>
                  <Kv rows={v.use} />
                </Card>
                <Card title={t('crm.at.23')} icon={IconUserCircle} hint={t('crm.at.47')}>
                  {v.people.map(([n, role]) => (
                    <div key={n} className={s.person}>
                      <Av text={n} />
                      <div>
                        <div>{n}</div>
                        <div className={s.lb}>{role}</div>
                      </div>
                    </div>
                  ))}
                </Card>
                <Card title={t('crm.at.24')} icon={IconLink} hint={t('crm.at.48')}>
                  {v.links.map((l) => (
                    <button key={l.k} type="button" className={s.ptLink} aria-label={l.ext ? `${l.k} ↗` : `${l.k} ›`} onClick={() => (l.ext ? toast({ msg: `${l.k} chưa mở ở bản này` }) : setTab('refund'))}>
                      <span>
                        <span className={s.ptLinkTx}>{l.k}</span>
                        <span className={s.lb}>{l.d}</span>
                      </span>
                      <IconExternalLink size={15} stroke={1.8} aria-hidden="true" />
                    </button>
                  ))}
                </Card>
              </div>
            </div>
          )}
          {p && v && tab === 'ct' && (
            <Card title={t('crm.pd.contacts', { n: v.contactRows.length })} label={t('crm.at.17')} icon={IconPhone} hint={t('crm.at.49')} actions={<Button onClick={noWrite} icon={<IconPlus size={14} aria-hidden="true" />}>{t('crm.cdt.themLienHe')}</Button>}>
              {v.contactRows.length === 0 && <div className={s.empty}>{t('crm.cdt.chuaCoLienHe3')}</div>}
              {v.contactRows.map((c, i) => (
                <div key={c.name} className={s.ptCt} data-first={i === 0 ? '' : undefined}>
                  <div>
                    <b>{c.name}</b>
                    <div className={s.lb}>{c.pos}</div>
                  </div>
                  <span className={s.lb}>—</span>
                  <span className={s.lb}>—</span>
                  <span className={s.lb}>{c.flags}</span>
                  <span className={s.ctAct}>
                    <button type="button" className={s.fold} aria-label={t('crm.pd.edit', { n: c.name })} title={t('crm.pd.edit', { n: c.name })} onClick={noWrite}><IconPencil size={16} stroke={1.8} aria-hidden="true" /></button>
                    <button type="button" className={s.fold} aria-label={t('crm.pd.del', { n: c.name })} title={t('crm.pd.del', { n: c.name })} onClick={noWrite}><IconTrash size={16} stroke={1.8} aria-hidden="true" /></button>
                  </span>
                </div>
              ))}
            </Card>
          )}
          {p && v && tab === 'bank' && (
            <Card title={t('crm.pd.banks', { n: v.bankRows.length })} label={t('crm.at.50')} icon={IconBuildingBank} hint={t('crm.at.51')} actions={<Button onClick={noWrite} icon={<IconPlus size={14} aria-hidden="true" />}>{t('crm.cdt.themTaiKhoan')}</Button>}>
              {v.bankRows.length === 0 && <div className={s.empty}>{t('crm.cdt.chuaCoTaiKhoan3')}</div>}
              {v.bankRows.map((b, i) => (
                <div key={b.bank} className={s.ptBk} data-first={i === 0 ? '' : undefined}>
                  <div>
                    <b>{b.bank}</b>
                    <div className={s.lb}>{b.pur}</div>
                  </div>
                  <div>
                    <b className={s.ptBkNo}>{b.no}</b>
                    <div className={s.lb}>{b.mode}</div>
                  </div>
                  <span className={s.ptBkHolder}>{b.holder}</span>
                  <span title={b.vsTip}>
                    <Chip tone={b.verified ? 'ok' : 'warn'} icon={b.verified ? <IconShieldCheck size={13} stroke={1.8} aria-hidden="true" /> : <IconClockHour3 size={13} stroke={1.8} aria-hidden="true" />}>{b.vs}</Chip>
                  </span>
                  <span className={s.ptBkAct}>
                    {b.canDef && <button type="button" className={s.fold} aria-label={t('crm.at.52')} title={t('crm.at.52')} onClick={noWrite}><IconStar size={16} stroke={1.8} aria-hidden="true" /></button>}
                    {b.canVerify && <Button onClick={noWrite} icon={<IconShieldCheck size={16} aria-hidden="true" />}>{t('crm.cdt.xacMinh')}</Button>}
                    <button type="button" className={s.fold} aria-label={t('crm.pd.pause', { n: b.bank })} title={t('crm.pd.pause', { n: b.bank })} onClick={noWrite}><IconPlayerPause size={16} stroke={1.8} aria-hidden="true" /></button>
                  </span>
                </div>
              ))}
            </Card>
          )}
          {p && v && tab === 'lcc' && (p.carrier ? (
            <Card title={t('crm.at.53')} label={t('crm.at.53')} icon={IconShip} hint={t('crm.at.54')}>
              <div className={s.ptLcc}>
                {v.lccSets.map((x) => (
                  <div key={x.code} className={s.ptLccSet}>
                    <b>{x.code}</b>
                    <div className={s.lb}>{x.d}</div>
                  </div>
                ))}
              </div>
              <Button variant="primary" onClick={() => toast({ msg: 'Thiết lập giá chưa mở ở bản này' })} icon={<IconEye size={16} aria-hidden="true" />}>{t('crm.cdt.xemVaSuaO')}</Button>
            </Card>
          ) : (
            <div className={s.ptNot}>{t('crm.cdt.localChargeChiAp')}</div>
          ))}
          {p && v && tab === 'portal' && (
            <Card title={t('crm.at.55')} label={t('crm.at.56')} icon={IconKey} hint={t('crm.at.57')} actions={<Button onClick={noWrite} icon={<IconPlus size={14} aria-hidden="true" />}>{t('crm.cdt.themTaiKhoan')}</Button>}>
              {v.portalRows.map((r, i) => {
                const on = !!shownPw[r.k];
                return (
                  <div key={r.k} className={s.ptPortal} data-first={i === 0 ? '' : undefined}>
                    <div>
                      <b>{r.k}</b>
                      <div className={s.lb}>{r.url}</div>
                    </div>
                    <span className={s.ptPortalUser}>{r.user}</span>
                    <span className={s.ptPortalPw}>{on ? 'MẪU-KHÔNG-THẬT' : '••••••••'}</span>
                    <span className={s.ptBkAct}>
                      <Button onClick={() => setShownPw({ ...shownPw, [r.k]: !on })} icon={on ? <IconEyeOff size={16} aria-hidden="true" /> : <IconEye size={16} aria-hidden="true" />} aria-label={t(on ? 'crm.pd.hidePw' : 'crm.pd.showPw', { k: r.k })}>{t(on ? 'crm.pd.hide' : 'crm.pd.show')}</Button>
                      <button type="button" className={s.fold} aria-label={t('crm.at.58')} title={t('crm.at.58')} onClick={() => toast({ msg: 'Đã chép mật khẩu · tự xoá khỏi bộ nhớ tạm sau 30 giây' })}><IconCopy size={16} stroke={1.8} aria-hidden="true" /></button>
                    </span>
                  </div>
                );
              })}
              {v.portalRows.length === 0 && <div className={s.empty}>{t('crm.cdt.chuaLuuTaiKhoan')}</div>}
            </Card>
          )}
          {p && v && tab === 'wiki' && (
            <div className={s.col}>
              <Card title={t('crm.at.59')} label={t('crm.at.59')} icon={IconPencil} hint={`Wiki của ${p.id} · ai trong team cũng đọc được`} actions={<Button onClick={noWrite} icon={<IconPlus size={14} aria-hidden="true" />}>{t('crm.cdt.ghiChu')}</Button>}>
                <div className={s.wkBody}>
                  {v.wikiNotes.length === 0 && <div className={s.empty}>{t('crm.cdt.chuaCoGhiChu')}</div>}
                  {v.wikiNotes.map((n, i) => (
                    <div key={n.title} className={`${s.wkNote} ${i ? s.rfBt : ''}`}>
                      <div className={s.cell2}>
                        <b style={{ fontWeight: 'var(--fw-label)' }}>{n.title}</b>
                        <span className={s.tx2} style={{ whiteSpace: 'pre-line' }}>{n.body}</span>
                        <span className={s.lb}>{n.meta}</span>
                      </div>
                      <span className={s.wkAct}>
                        <button type="button" className={s.wkBtn} aria-label={t('crm.pd.editNote', { n: n.title })} onClick={noWrite}><IconPencil size={16} stroke={1.8} aria-hidden="true" />{t('crm.cdt.sua')}</button>
                        <button type="button" className={s.wkBtn} aria-label={t('crm.pd.delNote', { n: n.title })} onClick={noWrite}><IconTrash size={16} stroke={1.8} aria-hidden="true" />{t('crm.cdt.xoa')}</button>
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
              <Card title={t('crm.at.60')} label={t('crm.at.60')} icon={IconFileText} hint={t('crm.at.61')} actions={<Button onClick={noWrite} icon={<IconPlus size={14} aria-hidden="true" />}>{t('crm.cdt.taiTep')}</Button>}>
                <div className={s.wkBody}>
                  {v.wikiFiles.length === 0 && <div className={s.empty}>{t('crm.cdt.chuaCoTepNao')}</div>}
                  {v.wikiFiles.map((f, i) => (
                    <div key={f.name} className={`${s.wkFile} ${i ? s.rfBt : ''}`}>
                      <span className={s.wkName}><span className={s.wkIc} aria-hidden="true"><IconFileText size={15} stroke={1.9} /></span><b>{f.name}</b></span>
                      <span className={s.tx2}>{f.kind}</span>
                      <span className={s.lb} style={{ fontSize: 'inherit' }}>{f.size}</span>
                      <span className={s.lb}>{f.meta}</span>
                      <span className={s.wkAct}>
                        <button type="button" className={s.wkBtn} aria-label={t('crm.pd.view', { n: f.name })} onClick={() => toast({ msg: t('crm.pd.noViewer') })}><IconSearch size={16} stroke={1.8} aria-hidden="true" />{t('crm.pd.viewBtn')}</button>
                        <button type="button" className={s.wkBtn} aria-label={t('crm.pd.del', { n: f.name })} onClick={noWrite}><IconTrash size={16} stroke={1.8} aria-hidden="true" />{t('crm.cdt.xoa')}</button>
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
          {p && v && tab === 'refund' && <RefundTab partner={p.id} code="KB" empty={!p.carrier} />}
          {p && v && tab === 'hist' && (
            <Card title={t('crm.at.62')} icon={IconActivity} hint={t('crm.at.63')}>
              <div className={s.ptHistTab}>
                {v.history.map((h) => (
                  <div key={h.k} className={s.ptHist}>
                    <span className={s.ptDot} data-tone={h.tone} aria-hidden="true" />
                    <div>
                      <div>{h.k}</div>
                      <div className={s.lb}>{h.who} · {h.when}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {p && v && tab === 'pay' && (() => {
            const pt = payT ?? p.payT;
            const net = netV ?? p.net;
            const preview = pt === 'net' ? `Hoá đơn 22/Oct/2026 → hạn trả ${net === '0' ? '22/Oct/2026 (trả ngay)' : `sau ${net} ngày`}` : pt === 'day' ? `Hoá đơn 22/Oct/2026 (trước ngày chốt ${cutV}) → trả trong tháng 10` : `Lô ETD 25/Oct/2026 → hạn trả ETD + ${etaV} ngày`;
            return (
              <Card title={t('crm.at.64')} label={t('crm.at.65')} icon={IconClockHour3} hint={t('crm.at.66')}>
                <div className={s.ptPay}>
                  <div>
                    <div className={s.ptPayLb}>{t('crm.cdt.cachTinhHanTra')}</div>
                    <div role="radiogroup" aria-label={t('crm.at.67')} className={s.ptPaySeg}>
                      {([['net', 'Số ngày sau hoá đơn'], ['day', 'Ngày chốt tháng'], ['eta', 'Theo ETA / ETD']] as const).map(([k, label]) => (
                        <button key={k} type="button" role="radio" aria-checked={pt === k} data-on={pt === k ? '' : undefined} onClick={() => setPayT(k)}>{label}</button>
                      ))}
                    </div>
                  </div>
                  {pt === 'net' && <label className={s.ptPayF}><div className={s.ptPayLb}>{t('crm.cdt.soNgaySauHoa')}</div><input value={net} onChange={(e) => setNetV(e.target.value)} /></label>}
                  {pt === 'day' && <label className={s.ptPayF}><div className={s.ptPayLb} title={t('crm.at.68')}>{t('crm.cdt.ngayChotTrongThang')}</div><input value={cutV} onChange={(e) => setCutV(e.target.value)} /></label>}
                  {pt === 'eta' && <label className={s.ptPayF}><div className={s.ptPayLb}>{t('crm.cdt.soNgayTinhTu')}</div><input value={etaV} onChange={(e) => setEtaV(e.target.value)} /></label>}
                  <div className={s.ptPayPv}>{preview}</div>
                  <div className={s.ptPaySave}>
                    <Button variant="primary" onClick={noWrite} icon={<IconDeviceFloppy size={16} aria-hidden="true" />}>{t('crm.cdt.luuDieuKhoan')}</Button>
                  </div>
                </div>
              </Card>
            );
          })()}
        </StateView>
      </div>
    </Page>
  );
}
